import React from "react";
import {
  Typography,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Box,
  Alert,
  Tooltip,
  IconButton,
  Container,
  Divider,
  Grid,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RefreshIcon from "@mui/icons-material/Refresh";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import color from "../../Constants/colors";
import Loading from "../Common/Loading";
import { useQueueLoad } from "../../Query/useMonitoring";
import { QueueLoadTaskItem, QueueLoadWorker } from "../../Api/monitoring";
import { useQueryClient } from "@tanstack/react-query";
import { formatTimeAgo } from "../../Helpers/dateHelpers";
import { shortTaskName } from "../../Helpers/stringHelpers";
import { useTranslation } from "react-i18next";

const InfoField: React.FC<{
  label: string;
  value: React.ReactNode;
  mono?: boolean;
  fullWidth?: boolean;
}> = ({ label, value, mono, fullWidth }) => (
  <Grid item xs={12} sm={fullWidth ? 12 : 6} md={fullWidth ? 12 : 4}>
    <Typography
      sx={{
        fontSize: "0.625rem",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        color: color.gray400,
        mb: 0.25,
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: "0.8125rem",
        color: color.textPrimary,
        fontFamily: mono ? "'JetBrains Mono', monospace" : undefined,
        wordBreak: "break-all",
      }}
    >
      {value}
    </Typography>
  </Grid>
);

const TaskItemCard: React.FC<{ item: QueueLoadTaskItem }> = ({ item }) => {
  const kwargsDisplay: Record<string, string> = {};
  if (item.kwargs) {
    const pick = [
      "schema_name",
      "log_name",
      "datasource_id",
      "image_path",
    ] as const;
    for (const key of pick) {
      if (key in item.kwargs) {
        const val = item.kwargs[key];
        if (typeof val === "object" && val !== null) {
          kwargsDisplay[key] = JSON.stringify(val);
        } else {
          kwargsDisplay[key] = String(val);
        }
      }
    }
    if (
      item.kwargs.properties_retriever &&
      typeof item.kwargs.properties_retriever === "object"
    ) {
      const pr = item.kwargs.properties_retriever as Record<string, unknown>;
      if (pr.datasource_id !== undefined)
        kwargsDisplay["datasource_id"] = String(pr.datasource_id);
      if (pr.instance_id !== undefined)
        kwargsDisplay["instance_id"] = String(pr.instance_id);
    }
  }

  const argsDisplay =
    item.args && item.args.length > 0 ? item.args.join(", ") : null;

  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{
        backgroundColor: color.gray50,
        border: `1px solid ${color.gray200}`,
        borderRadius: "8px !important",
        mb: 1,
        "&:before": { display: "none" },
        overflow: "hidden",
      }}
    >
      <AccordionSummary
        expandIcon={
          <ExpandMoreIcon sx={{ color: color.gray400, fontSize: "1.1rem" }} />
        }
        sx={{
          px: 2,
          py: 0,
          minHeight: 44,
          "&.Mui-expanded": { minHeight: 44 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            width: "100%",
          }}
        >
          <Tooltip title={item.name} arrow>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "0.8125rem",
                color: color.primary_dark,
              }}
            >
              {shortTaskName(item.name)}
            </Typography>
          </Tooltip>

          <Chip
            label={item.delivery_info.routing_key}
            size="small"
            sx={{
              backgroundColor: color.accent,
              color: color.white,
              fontWeight: 700,
              fontSize: "0.625rem",
              height: 20,
            }}
          />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              ml: "auto",
              color: color.gray400,
            }}
          >
            <AccessTimeIcon sx={{ fontSize: "0.875rem" }} />
            <Typography sx={{ fontSize: "0.75rem" }}>
              {formatTimeAgo(item.time_start)}
            </Typography>
          </Box>
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
        <Divider sx={{ mb: 1.5, borderColor: color.gray200 }} />
        <Grid container spacing={1.5}>
          <InfoField label="Task ID" value={item.id} mono />
          <InfoField label="Worker PID" value={item.worker_pid} />
          <InfoField label="Queue" value={item.delivery_info.routing_key} />
          <InfoField label="Priority" value={item.delivery_info.priority} />
          <InfoField
            label="Acknowledged"
            value={item.acknowledged ? "Yes" : "No"}
          />
          <InfoField
            label="Redelivered"
            value={item.delivery_info.redelivered ? "Yes" : "No"}
          />

          {argsDisplay && (
            <InfoField label="Args" value={`[${argsDisplay}]`} mono fullWidth />
          )}

          {Object.keys(kwargsDisplay).length > 0 && (
            <Grid item xs={12}>
              <Typography
                sx={{
                  fontSize: "0.625rem",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  color: color.gray400,
                  mb: 0.75,
                }}
              >
                Parameters
              </Typography>
              <Box
                sx={{
                  backgroundColor: color.white,
                  border: `1px solid ${color.gray200}`,
                  borderRadius: "6px",
                  p: 1.5,
                }}
              >
                <Grid container spacing={1}>
                  {Object.entries(kwargsDisplay).map(([key, val]) => (
                    <Grid item xs={12} sm={6} key={key}>
                      <Typography
                        sx={{
                          fontSize: "0.625rem",
                          fontWeight: 600,
                          color: color.gray400,
                          textTransform: "uppercase",
                          letterSpacing: "0.04em",
                        }}
                      >
                        {key}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          color: color.textPrimary,
                          fontFamily: "'JetBrains Mono', monospace",
                          wordBreak: "break-all",
                        }}
                      >
                        {val}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Grid>
          )}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

const WorkerSection: React.FC<{ worker: QueueLoadWorker }> = ({ worker }) => {
  const runningEntries = Object.entries(worker.running.by_task);
  const waitingEntries = Object.entries(worker.waiting_on_worker.by_task);
  const { t } = useTranslation("monitoring");

  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{
        backgroundColor: color.white,
        border: `1px solid ${color.gray200}`,
        borderRadius: "10px !important",
        mb: 1.5,
        "&:before": { display: "none" },
        overflow: "hidden",
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: color.secondary }} />}
        sx={{
          px: 2.5,
          py: 0.5,
          minHeight: 56,
          "&.Mui-expanded": { minHeight: 56 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            width: "100%",
            flexWrap: "wrap",
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, color: color.primary_dark, minWidth: 160 }}
          >
            {worker.name}
          </Typography>

          <Box sx={{ display: "flex", gap: 0.5 }}>
            {worker.queues.map((q) => (
              <Chip
                key={q}
                label={q}
                size="small"
                sx={{
                  backgroundColor: `${color.secondary}14`,
                  color: color.secondary,
                  fontWeight: 600,
                  fontSize: "0.6875rem",
                  border: `1px solid ${color.secondary}30`,
                }}
              />
            ))}
          </Box>

          <Box sx={{ display: "flex", gap: 1, ml: "auto" }}>
            <Chip
              label={`${worker.running.total} running`}
              size="small"
              sx={{
                backgroundColor:
                  worker.running.total > 0 ? color.green50 : color.gray50,
                color:
                  worker.running.total > 0 ? color.green700 : color.gray400,
                fontWeight: 600,
                fontSize: "0.6875rem",
                border: `1px solid ${worker.running.total > 0 ? color.green200 : color.gray200}`,
              }}
            />
            <Chip
              label={`${worker.waiting_on_worker.total} waiting`}
              size="small"
              sx={{
                backgroundColor:
                  worker.waiting_on_worker.total > 0
                    ? color.yellow50
                    : color.gray50,
                color:
                  worker.waiting_on_worker.total > 0
                    ? color.yellow700
                    : color.gray400,
                fontWeight: 600,
                fontSize: "0.6875rem",
                border: `1px solid ${worker.waiting_on_worker.total > 0 ? "#fef08a" : color.gray200}`,
              }}
            />
          </Box>
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ px: 2.5, pb: 2.5, pt: 0 }}>
        <Divider sx={{ mb: 2, borderColor: color.gray200 }} />

        {(runningEntries.length > 0 || waitingEntries.length > 0) && (
          <Box sx={{ mb: 2 }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "0.75rem",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                color: color.gray400,
                mb: 1,
              }}
            >
              Task Summary
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              {runningEntries.map(([task, count]) => (
                <Chip
                  key={`run-${task}`}
                  label={`${shortTaskName(task)}: ${count}`}
                  size="small"
                  sx={{
                    backgroundColor: color.green50,
                    color: color.green700,
                    fontWeight: 600,
                    fontSize: "0.6875rem",
                    border: `1px solid ${color.green200}`,
                  }}
                />
              ))}
              {waitingEntries.map(([task, count]) => (
                <Chip
                  key={`wait-${task}`}
                  label={`${shortTaskName(task)}: ${count} waiting`}
                  size="small"
                  sx={{
                    backgroundColor: color.yellow50,
                    color: color.yellow700,
                    fontWeight: 600,
                    fontSize: "0.6875rem",
                    border: `1px solid ${color.yellow200}`,
                  }}
                />
              ))}
            </Box>
          </Box>
        )}
        {worker.running.items.length > 0 && (
          <Box>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "0.75rem",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                color: color.gray400,
                mb: 1,
              }}
            >
              {t("runningTasks")}
            </Typography>
            {worker.running.items.map((item) => (
              <TaskItemCard key={item.id} item={item} />
            ))}
          </Box>
        )}

        {worker.running.items.length === 0 &&
          worker.waiting_on_worker.total === 0 && (
            <Typography sx={{ fontSize: "0.8125rem", color: color.gray400 }}>
              {t("noTasks")}
            </Typography>
          )}
      </AccordionDetails>
    </Accordion>
  );
};

const QueueLoadMonitoring: React.FC = () => {
  const { data, isLoading, isError, error, dataUpdatedAt } = useQueueLoad();
  const queryClient = useQueryClient();
  const { t } = useTranslation("monitoring");

  if (isLoading) {
    return (
      <Loading
        backgroundColor={color.gray100}
        color1={color.accent}
        color2={color.secondary}
      />
    );
  }

  if (isError) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Alert severity="error">
          {t("failedToLoadQueueData", {
            message: (error as Error)?.message || t("unknownError"),
          })}
        </Alert>
      </Container>
    );
  }

  if (!data) return null;

  const lastUpdated = dataUpdatedAt
    ? new Date(dataUpdatedAt).toLocaleTimeString()
    : "";

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 64px)",
        backgroundColor: color.gray50,
        py: 4,
        px: 2,
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Typography
            variant="h4"
            sx={{ fontWeight: 700, color: color.primary_dark, mb: 0.5 }}
          >
            {t("queueLoadMonitoring")}
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <Typography variant="caption" sx={{ color: color.gray400 }}>
              {t("autoRefresh", { time: lastUpdated })}
            </Typography>
            <Tooltip title={t("refreshNow")}>
              <IconButton
                size="small"
                onClick={() =>
                  queryClient.invalidateQueries({ queryKey: ["queueLoad"] })
                }
                sx={{
                  color: color.secondary,
                  "&:hover": { color: color.accent },
                }}
              >
                <RefreshIcon sx={{ fontSize: "1rem" }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 2.5,
            justifyContent: "center",
            flexWrap: "wrap",
            mb: 4,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: "12px",
              textAlign: "center",
              flex: "1 1 220px",
              maxWidth: 280,
              backgroundColor: color.white,
              border: `1px solid ${color.gray200}`,
            }}
          >
            <Typography
              sx={{
                fontSize: "0.75rem",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                color: color.gray400,
                mb: 1,
              }}
            >
              {t("runningTotal")}
            </Typography>
            <Typography
              sx={{
                fontFamily: "Montserrat, sans-serif",
                fontWeight: 800,
                fontSize: "2.5rem",
                lineHeight: 1,
                color: color.green700,
              }}
            >
              {data.totals.running_total}
            </Typography>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: "12px",
              textAlign: "center",
              flex: "1 1 220px",
              maxWidth: 280,
              backgroundColor: color.white,
              border: `1px solid ${color.gray200}`,
            }}
          >
            <Typography
              sx={{
                fontSize: "0.75rem",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                color: color.gray400,
                mb: 1,
              }}
            >
              {t("waitingOnWorkers")}
            </Typography>
            <Typography
              sx={{
                fontFamily: "Montserrat, sans-serif",
                fontWeight: 800,
                fontSize: "2.5rem",
                lineHeight: 1,
                color:
                  data.totals.waiting_on_workers_total > 0
                    ? color.yellow700
                    : color.textSecondary,
              }}
            >
              {data.totals.waiting_on_workers_total}
            </Typography>
          </Paper>
        </Box>

        <Box>
          <Typography
            sx={{
              fontFamily: "Montserrat, sans-serif",
              fontWeight: 700,
              fontSize: "1.125rem",
              color: color.primary_dark,
              mb: 2,
            }}
          >
            {t("workers", { count: data.workers.length })}
          </Typography>

          {data.workers.map((worker) => (
            <WorkerSection key={worker.name} worker={worker} />
          ))}

          {data.workers.length === 0 && (
            <Paper
              elevation={0}
              sx={{
                p: 4,
                textAlign: "center",
                borderRadius: "10px",
                border: `1px solid ${color.gray200}`,
                backgroundColor: color.white,
              }}
            >
              <Typography sx={{ color: color.gray400 }}>
                {t("noWorkersOnline")}
              </Typography>
            </Paper>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default QueueLoadMonitoring;
